import { elementIntersectsBound, getBoundWithRotation } from './geometry';
import type { SurfaceModel } from './SurfaceModel';
import type { GfxElement, IBound, IVec } from './types';

/** 选择管理器：维护选中集合、框选与选中包围盒。 */
export class SelectionManager {
  private selected = new Set<string>();
  private listeners = new Set<() => void>();

  constructor(private surface: SurfaceModel) {}

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  get ids(): string[] {
    return [...this.selected];
  }

  get elements(): GfxElement[] {
    return this.ids.map(id => this.surface.get(id)).filter(Boolean) as GfxElement[];
  }

  get size(): number {
    return this.selected.size;
  }

  has(id: string): boolean {
    return this.selected.has(id);
  }

  isOnly(id: string): boolean {
    return this.selected.size === 1 && this.selected.has(id);
  }

  set(ids: string[]): void {
    this.selected = new Set(ids.filter(id => this.surface.has(id)));
    this.emit();
  }

  add(id: string): void {
    if (!this.surface.has(id)) return;
    this.selected.add(id);
    this.emit();
  }

  toggle(id: string): void {
    if (this.selected.has(id)) this.selected.delete(id);
    else this.selected.add(id);
    this.emit();
  }

  clear(): void {
    if (this.selected.size === 0) return;
    this.selected.clear();
    this.emit();
  }

  /** 清理已删除的元素。 */
  prune(): void {
    let changed = false;
    for (const id of [...this.selected]) {
      if (!this.surface.has(id)) {
        this.selected.delete(id);
        changed = true;
      }
    }
    if (changed) this.emit();
  }

  /** 选中集合的包围盒。 */
  get bound(): IBound | null {
    const bounds = this.elements
      .filter(el => el.type !== 'connector' && el.type !== 'rope' && el.type !== 'pin')
      .map(el => getBoundWithRotation(el));
    if (bounds.length === 0) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const b of bounds) {
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.w);
      maxY = Math.max(maxY, b.y + b.h);
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  /** 框选。 */
  selectInBound(bound: IBound, additive = false): void {
    const hits = this.surface
      .elementsInBound(bound)
      .filter(el => elementIntersectsBound(el, bound, this.surface.lookup));
    if (additive) {
      for (const el of hits) this.selected.add(el.id);
      this.emit();
    } else {
      this.set(hits.map(el => el.id));
    }
  }

  /** 命中测试并选择。 */
  selectAt(point: IVec, additive = false): GfxElement | null {
    const hit = this.surface.hitTest(point);
    if (!hit) {
      if (!additive) this.clear();
      return null;
    }
    // 若命中分组内的元素，向上选中整个分组
    const groupId = this.surface.groupOf(hit.id);
    const target = groupId ? this.surface.get(groupId) ?? hit : hit;
    if (additive) this.toggle(target.id);
    else this.set([target.id]);
    return target;
  }
}